import { AddressInfo } from 'models/restaurant'
import { getChartColor } from './chartColors'

export interface DemographicItem {
  name: string
  value: number
  color: string
  percentage: number
}

// Demographic field groups for cleaner mapping
export const DEMOGRAPHIC_GROUPS = {
  race: {
    White: 'race_white_pop_pct',
    Black: 'race_blackorafricanamerican_pop_pct',
    Asian: 'race_asian_pop_pct',
    Multiracial: 'race_twoormoreraces_pop_pct',
    Other: [
      'race_otherrace_pop_pct',
      'race_americanindianandalaskanative_pop_pct',
      'race_nativehawaiianandotherpacificislander_pop_pct',
    ],
  },
  age: {
    '0-19': [
      'age_5to9years_pop_pct',
      'age_10to14years_pop_pct',
      'age_15to19years_pop_pct',
    ],
    '20-34': ['age_20to24years_pop_pct', 'age_25to34years_pop_pct'],
    '35-64': [
      'age_35to44years_pop_pct',
      'age_45to54years_pop_pct',
      'age_55to59years_pop_pct',
      'age_60to64years_pop_pct',
    ],
    '65+': [
      'age_65to74years_pop_pct',
      'age_75to84years_pop_pct',
      'age_85yearsandover_pop_pct',
    ],
  },
  education: {
    'High School and Below': [
      'educationlevel_lessthan9thgrade_pop_pct',
      'educationlevel_9thto12thgrade_nodiploma_pop_pct',
      'educationlevel_highschoolgraduate_pop_pct',
    ],
    "Some College / Associate's": [
      'educationlevel_somecollege_nodegree_pop_pct',
      'educationlevel_associatesdegree_pop_pct',
    ],
    "Bachelor's Degree": ['educationlevel_bachelorsdegree_pop_pct'],
    'Graduate or Professional Degree': [
      'educationlevel_graduateorprofessionaldegree_pop_pct',
    ],
  },
  income: {
    '100k+': [
      'income_100000to149999_hh_pct',
      'income_150000to199999_hh_pct',
      'income_200000ormore_hh_pct',
    ],
  },
}

// Helper to safely get field value from address info
function getFieldValue(addressInfo: AddressInfo, field: string): number {
  return (addressInfo as any)[field] || 0
}

// Helper to sum multiple fields
function sumFields(addressInfo: AddressInfo, fields: string[]): number {
  return fields.reduce(
    (sum, field) => sum + getFieldValue(addressInfo, field),
    0
  )
}

// Generic function to transform demographic data
export function transformDemographicData(
  addressInfo: AddressInfo,
  groupDefinition: Record<string, string | string[]>
): DemographicItem[] {
  return Object.entries(groupDefinition)
    .map(([name, fields], index) => {
      const value = Array.isArray(fields)
        ? sumFields(addressInfo, fields)
        : getFieldValue(addressInfo, fields)

      return {
        name,
        value,
        color: getChartColor(index),
        percentage: Math.round(value * 100),
      }
    })
    .filter((item) => item.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage)
}

// Specific demographic data transformers
export function getRaceData(addressInfo: AddressInfo): DemographicItem[] {
  return transformDemographicData(addressInfo, DEMOGRAPHIC_GROUPS.race)
}

export function getAgeData(addressInfo: AddressInfo): DemographicItem[] {
  // For age, maintain the order as defined (by age group), don't sort by percentage
  const ageGroups = Object.entries(DEMOGRAPHIC_GROUPS.age).map(
    ([name, fields], index) => {
      const value = Array.isArray(fields)
        ? sumFields(addressInfo, fields)
        : getFieldValue(addressInfo, fields)

      return {
        name,
        value,
        color: getChartColor(index),
        percentage: Math.round(value * 100),
      }
    }
  )

  // Calculate total and adjust if needed to ensure 100%
  const total = ageGroups.reduce((sum, item) => sum + item.percentage, 0)

  if (total < 100 && total > 0) {
    // Add the missing percentage to the first group (0-19)
    const missing = 100 - total
    ageGroups[0].percentage += missing
    ageGroups[0].value += missing / 100
  }

  return ageGroups.filter((item) => item.percentage > 0)
}

export function getEducationData(addressInfo: AddressInfo): DemographicItem[] {
  return transformDemographicData(addressInfo, DEMOGRAPHIC_GROUPS.education)
}

export function getHighIncomePercentage(addressInfo: AddressInfo): number {
  return sumFields(addressInfo, DEMOGRAPHIC_GROUPS.income['100k+'])
}
